import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { Check, Loader2, Search } from 'lucide-react'
import { usePostings, useLinkExistingEntry } from './hooks/useReconApi'
import type { Transaction, MiragePosting } from './types'
import { formatPostingAmount } from './format'

interface LinkTabProps {
  transaction: Transaction
  clientId: string
  bankAccountId: number
  bankAccountLabel: string
}

export function LinkTab({ transaction, clientId, bankAccountId }: LinkTabProps) {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const txnAmount = Math.abs(transaction.amount)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  const { data: postings = [], isLoading } = usePostings(
    clientId,
    bankAccountId,
    debouncedSearch ? { search: debouncedSearch } : undefined
  )
  const linkMutation = useLinkExistingEntry()

  const filtered = postings

  const selected = postings.find((p) => p.acc_rec_d_id === selectedId)

  const handleLink = () => {
    if (!selectedId) return
    linkMutation.mutate({
      transaction_id: transaction.id,
      client_id: clientId,
      mirage_bank_account_id: bankAccountId,
      acc_rec_d_id: selectedId,
    })
  }

  // Match if either the base amount or the foreign amount on the posting
  // equals the bank transaction amount. Lets a EUR-base posting that
  // recorded "188 USD" surface as an exact match for a 188 USD bank txn.
  const isAmountMatch = (posting: MiragePosting) => {
    const base = Math.abs(parseFloat(posting.amount))
    if (Math.abs(base - txnAmount) < 0.01) return true
    if (posting.foreign_amount) {
      const fa = Math.abs(parseFloat(posting.foreign_amount))
      if (Math.abs(fa - txnAmount) < 0.01) return true
    }
    return false
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-neutral-500">
        Select an existing journal entry from Mirage to link to this transaction.
        The entry was already posted manually — this just marks it as reconciled.
      </p>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
        <Input
          placeholder="Search by counterparty, invoice #, note, or account..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="max-h-[280px] overflow-y-auto rounded border border-neutral-200">
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-neutral-400">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading journal entries...
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-4 text-center text-sm text-neutral-400">
            {search
              ? 'No entries match your search.'
              : 'No journal entries found on this account.'}
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-neutral-50 text-left text-neutral-500">
              <tr>
                <th className="px-2 py-1.5 font-medium">Date</th>
                <th className="px-2 py-1.5 font-medium">DT</th>
                <th className="px-2 py-1.5 font-medium">CT</th>
                <th className="px-2 py-1.5 font-medium text-right">Amount</th>
                <th className="px-2 py-1.5 font-medium">Counterparty</th>
                <th className="px-2 py-1.5 font-medium">Invoice #</th>
                <th className="px-2 py-1.5 font-medium">Note</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtered.map((p) => {
                const amt = parseFloat(p.amount)
                const match = isAmountMatch(p)
                return (
                  <tr
                    key={p.acc_rec_d_id}
                    className={cn(
                      'cursor-pointer hover:bg-blue-50',
                      selectedId === p.acc_rec_d_id && 'bg-blue-50 font-medium',
                      match && selectedId !== p.acc_rec_d_id && 'bg-green-50/50'
                    )}
                    onClick={() => setSelectedId(p.acc_rec_d_id)}
                  >
                    <td className="whitespace-nowrap px-2 py-1.5 text-neutral-600">
                      {p.reg_date || p.doc_date || '—'}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 font-mono text-neutral-700">
                      {p.dt_account_code}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 font-mono text-neutral-700">
                      {p.ct_account_code}
                    </td>
                    <td className={cn(
                      'whitespace-nowrap px-2 py-1.5 text-right font-mono',
                      amt >= 0 ? 'text-green-700' : 'text-red-600'
                    )}>
                      {(() => {
                        const fmt = formatPostingAmount(p)
                        return (
                          <span>
                            {fmt.primary}
                            {fmt.secondary && (
                              <span className="ml-1 text-[10px] text-neutral-400">
                                · {fmt.secondary}
                              </span>
                            )}
                            {match && (
                              <span className="ml-1 text-[10px] text-green-600">exact</span>
                            )}
                          </span>
                        )
                      })()}
                    </td>
                    <td className="max-w-[180px] truncate px-2 py-1.5 text-neutral-600">
                      {p.counterparty_name || '—'}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-neutral-500">
                      {p.invoice_doc_num || p.doc_nom || '—'}
                    </td>
                    <td className="max-w-[200px] truncate px-2 py-1.5 text-neutral-500">
                      {p.note || '—'}
                    </td>
                    <td className="px-1 py-1.5">
                      {selectedId === p.acc_rec_d_id && (
                        <Check className="h-3.5 w-3.5 text-blue-600" />
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <div className="rounded border border-blue-200 bg-blue-50/50 px-3 py-2 text-xs text-blue-800">
          Selected: AccRec_D #{selected.acc_rec_d_id} — DT {selected.dt_account_code} / CT {selected.ct_account_code} — {(() => {
            const fmt = formatPostingAmount(selected)
            return fmt.secondary ? `${fmt.primary} · ${fmt.secondary}` : fmt.primary
          })()}
          {selected.note && <span className="ml-2 text-blue-600">({selected.note})</span>}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="blue"
          disabled={!selectedId || linkMutation.isLoading}
          onClick={handleLink}
        >
          {linkMutation.isLoading ? 'Linking...' : 'Link to this entry'}
        </Button>
        {linkMutation.isError && (
          <span className="text-sm text-red-600">
            {(linkMutation.error as Error).message}
          </span>
        )}
      </div>
    </div>
  )
}
